import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Zap,
    Flame,
    Upload,
    Loader2,
    ArrowRight,
    Sparkles,
    CheckCircle2,
    TrendingDown,
    Leaf,
    X,
    Users,
    FileText,
    IdCard,
    Heart,
    Mail,
    Phone,
    CreditCard,
    Receipt,
    Plus,
    AlertCircle,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function EnergyQuoter() {
    const navigate = useNavigate();
    const [mode, setMode] = useState("manual"); // 'manual' | 'bill'
    const [tipo, setTipo] = useState("luce");
    const [data, setData] = useState({
        operatore: "",
        kwh: "",
        smc: "",
        fissa: "",
        consumi: "",
        cliente: "",
    });
    const [billFileId, setBillFileId] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisNote, setAnalysisNote] = useState("");
    const [billBreakdown, setBillBreakdown] = useState(null);
    const [showBreakdown, setShowBreakdown] = useState(false);

    const [offers, setOffers] = useState([]);
    const [selectedOfferId, setSelectedOfferId] = useState(null);
    const [clientPhone, setClientPhone] = useState("");
    const [clientEmail, setClientEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [stats, setStats] = useState(null);
    const [showClientNameError, setShowClientNameError] = useState(false);
    const [clientDocs, setClientDocs] = useState([]); // [{file_id, filename, size, content_type}]
    const [uploadingDoc, setUploadingDoc] = useState(false);

    // Load offers for current tipo
    useEffect(() => {
        api.get(`/energy/offers?tipo=${tipo}`).then(({ data }) => {
            setOffers(data);
            setSelectedOfferId(null);
        });
    }, [tipo]);

    // Load energy stats once
    useEffect(() => {
        api.get("/energy/stats")
            .then(({ data }) => setStats(data))
            .catch(() => {});
    }, []);

    const handleBillUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setAnalyzing(true);
        setAnalysisNote("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data: res } = await api.post("/energy/analyze-bill", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 90000,
            });
            setBillFileId(res.bill_file_id || null);
            setTipo(res.tipo || tipo);
            setData((prev) => ({
                ...prev,
                operatore: res.operatore || prev.operatore,
                kwh: res.quota_energia_eur_per_kwh != null ? String(res.quota_energia_eur_per_kwh) : prev.kwh,
                smc: res.quota_gas_eur_per_smc != null ? String(res.quota_gas_eur_per_smc) : prev.smc,
                fissa: res.quota_fissa_eur_per_mese != null ? String(res.quota_fissa_eur_per_mese) : prev.fissa,
                consumi:
                    res.consumi_annui_kwh != null
                        ? String(res.consumi_annui_kwh)
                        : res.consumi_annui_smc != null
                        ? String(res.consumi_annui_smc)
                        : prev.consumi,
                cliente: res.cliente || prev.cliente,
            }));
            setAnalysisNote(res.note || "");
            setBillBreakdown(res);
            setShowBreakdown(true);
            toast.success(
                res.low_confidence
                    ? "Analisi completata · controlla i valori (bassa confidenza)"
                    : "Bolletta analizzata con successo",
            );
        } catch (err) {
            toast.error(err.response?.data?.detail || "Analisi bolletta fallita");
        } finally {
            setAnalyzing(false);
        }
    };

    // Current monthly cost estimate
    const currentMonthly = useMemo(() => {
        const fissa = parseFloat(data.fissa) || 0;
        const consumi = parseFloat(data.consumi) || 0;
        const rate = tipo === "gas" ? parseFloat(data.smc) || 0 : parseFloat(data.kwh) || 0;
        const variable = (rate * consumi) / 12;
        return fissa + variable;
    }, [data, tipo]);

    const hasData = useMemo(() => {
        const rate = tipo === "gas" ? parseFloat(data.smc) : parseFloat(data.kwh);
        return !!rate && !!parseFloat(data.consumi);
    }, [data, tipo]);

    const calcSavings = (offer) => {
        const consumi = parseFloat(data.consumi) || 0;
        if (!consumi) return null;
        const offerRate =
            tipo === "gas" ? offer.price_per_smc || 0 : offer.price_per_kwh || 0;
        const newMonthly = (offerRate * consumi) / 12 + (offer.monthly_fee || 0);
        const saving = currentMonthly - newMonthly;
        return { newMonthly, saving, annualSaving: saving * 12 };
    };

    const handleClientDocUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        e.target.value = ""; // reset for re-selection of same file
        setUploadingDoc(true);
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                const { data: res } = await api.post(
                    "/energy/upload-client-doc",
                    fd,
                    { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 },
                );
                setClientDocs((prev) => [
                    ...prev,
                    {
                        file_id: res.id,
                        filename: res.filename,
                        size: res.size,
                        content_type: res.content_type,
                    },
                ]);
            }
            toast.success(
                files.length === 1 ? "Documento caricato" : `${files.length} documenti caricati`,
            );
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload fallito");
        } finally {
            setUploadingDoc(false);
        }
    };

    const removeClientDoc = (fileId) => {
        setClientDocs((prev) => prev.filter((d) => d.file_id !== fileId));
    };

    const submit = async () => {
        if (!selectedOfferId) {
            toast.error("Seleziona un'offerta prima di inviare");
            return;
        }
        if (!data.cliente.trim()) {
            setShowClientNameError(true);
            toast.error("Il nome del cliente è obbligatorio");
            // Scroll alla sezione dati
            document.querySelector('[data-testid="field-cliente"]')
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                client_name: data.cliente.trim(),
                client_phone: clientPhone || null,
                client_email: clientEmail || null,
                tipo,
                current_operator: data.operatore || null,
                current_price_per_kwh:
                    tipo !== "gas" && data.kwh ? parseFloat(data.kwh) : null,
                current_price_per_smc:
                    tipo === "gas" && data.smc ? parseFloat(data.smc) : null,
                current_monthly_fee: data.fissa ? parseFloat(data.fissa) : null,
                annual_consumption_kwh:
                    tipo !== "gas" && data.consumi ? parseFloat(data.consumi) : null,
                annual_consumption_smc:
                    tipo === "gas" && data.consumi ? parseFloat(data.consumi) : null,
                bill_file_id: billFileId,
                extraction_source: mode === "bill" ? "bill" : "manual",
                selected_offer_id: selectedOfferId,
                client_documents: clientDocs.map((d) => ({
                    file_id: d.file_id,
                    filename: d.filename,
                    size: d.size,
                    content_type: d.content_type,
                })),
            };
            const { data: res } = await api.post("/energy/switch-request", payload);
            toast.success("Richiesta di switch inviata alla sede");
            navigate(`/richieste/${res.id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Invio fallito");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto space-y-8" data-testid="energy-quoter-page">
            {/* Hero */}
            <div className="relative bg-[#0C4650] text-white rounded-3xl overflow-hidden p-8 sm:p-10">
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 80% 20%, #C7FF2C 0%, transparent 40%)",
                    }}
                />
                <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] text-[#C7FF2C] font-black">
                            <Zap className="w-3 h-3" /> ENERGY QUOTER
                        </div>
                        <h1 className="mt-2 text-3xl sm:text-5xl font-black leading-tight">
                            Trova l'offerta <span className="text-[#C7FF2C]">perfetta</span>
                        </h1>
                        <p className="mt-2 text-white/70 max-w-lg">
                            Inserisci i costi attuali del cliente o carica una bolletta.
                            Ti mostriamo risparmio stimato e le migliori offerte del mercato.
                        </p>
                    </div>
                </div>

                {/* Live savings ribbon */}
                {stats && (stats.avg_monthly_savings > 0 || stats.switch_requests_last_30d > 0) && (
                    <div
                        className="relative z-10 mt-6 grid sm:grid-cols-3 gap-3"
                        data-testid="energy-stats-ribbon"
                    >
                        <StatBox
                            icon={<TrendingDown className="w-4 h-4" />}
                            label="Risparmio medio/mese"
                            value={`€ ${stats.avg_monthly_savings.toFixed(2)}`}
                            highlight
                            testid="stat-avg-monthly"
                        />
                        <StatBox
                            icon={<Sparkles className="w-4 h-4" />}
                            label="Risparmio medio/anno"
                            value={`€ ${stats.avg_annual_savings.toFixed(0)}`}
                            testid="stat-avg-annual"
                        />
                        <StatBox
                            icon={<Users className="w-4 h-4" />}
                            label="Switch inviati (30 gg)"
                            value={stats.switch_requests_last_30d}
                            testid="stat-requests-30d"
                        />
                    </div>
                )}
            </div>

            {/* Step 1: tipo + mode */}
            <div className="card p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
                    <h2 className="text-xl font-black text-[#0C4650]">1. Dati attuali del cliente</h2>
                    <div className="flex gap-2">
                        <TypeBtn
                            active={tipo === "luce"}
                            onClick={() => setTipo("luce")}
                            icon={<Zap className="w-4 h-4" />}
                            label="Luce"
                            testid="tipo-luce"
                        />
                        <TypeBtn
                            active={tipo === "gas"}
                            onClick={() => setTipo("gas")}
                            icon={<Flame className="w-4 h-4" />}
                            label="Gas"
                            testid="tipo-gas"
                        />
                    </div>
                </div>

                <div className="flex gap-2 mb-6 border-b border-gray-100">
                    <TabBtn
                        active={mode === "manual"}
                        onClick={() => setMode("manual")}
                        label="Inserisci manualmente"
                        testid="mode-manual"
                    />
                    <TabBtn
                        active={mode === "bill"}
                        onClick={() => setMode("bill")}
                        label="Carica bolletta"
                        testid="mode-bill"
                    />
                </div>

                {mode === "bill" && (
                    <div className="mb-5">
                        <label
                            className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#0C4650]/30 hover:border-[#0C4650] rounded-xl py-8 px-4 cursor-pointer transition-colors bg-[#f7f7f5]"
                            data-testid="bill-dropzone"
                        >
                            {analyzing ? (
                                <>
                                    <Loader2 className="w-6 h-6 text-[#0C4650] animate-spin" />
                                    <span className="text-sm font-semibold text-[#0C4650]">
                                        Analisi in corso… (può richiedere qualche secondo)
                                    </span>
                                </>
                            ) : (
                                <>
                                    <Upload className="w-6 h-6 text-[#0C4650]" />
                                    <span className="text-sm font-bold text-[#0C4650]">
                                        Carica la bolletta (PDF, JPG, PNG, WEBP)
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        Estrazione automatica via AI · anche screenshot
                                    </span>
                                </>
                            )}
                            <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                className="hidden"
                                onChange={handleBillUpload}
                                disabled={analyzing}
                                data-testid="bill-file-input"
                            />
                        </label>
                        {analysisNote && (
                            <p className="text-xs text-gray-500 mt-2 italic">
                                Nota AI: {analysisNote}
                            </p>
                        )}
                        {billBreakdown && (
                            <BillBreakdownPanel
                                data={billBreakdown}
                                tipo={tipo}
                                expanded={showBreakdown}
                                onToggle={() => setShowBreakdown((v) => !v)}
                            />
                        )}
                    </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Nome cliente *" testid="field-cliente">
                        <input
                            className={`field ${
                                showClientNameError && !data.cliente.trim()
                                    ? "border-2 border-red-500"
                                    : ""
                            }`}
                            value={data.cliente}
                            onChange={(e) => {
                                setData({ ...data, cliente: e.target.value });
                                if (e.target.value.trim()) setShowClientNameError(false);
                            }}
                            placeholder="Mario Rossi"
                            data-testid="input-cliente"
                        />
                        {showClientNameError && !data.cliente.trim() && (
                            <div className="mt-1 inline-flex items-center gap-1 text-xs text-red-600 font-bold"
                                 data-testid="cliente-error">
                                <AlertCircle className="w-3 h-3" />
                                Il nome del cliente è obbligatorio
                            </div>
                        )}
                    </Field>
                    <Field label="Operatore attuale (opzionale)" testid="field-operatore">
                        <input
                            className="field"
                            value={data.operatore}
                            onChange={(e) => setData({ ...data, operatore: e.target.value })}
                            placeholder="Es. Enel Energia"
                        />
                    </Field>
                    {tipo === "gas" ? (
                        <Field label="Costo al m³ (€/Smc) *" testid="field-smc">
                            <input
                                className="field"
                                value={data.smc}
                                onChange={(e) => setData({ ...data, smc: e.target.value })}
                                placeholder="0.42"
                                inputMode="decimal"
                            />
                        </Field>
                    ) : (
                        <Field label="Costo al kWh (€) *" testid="field-kwh">
                            <input
                                className="field"
                                value={data.kwh}
                                onChange={(e) => setData({ ...data, kwh: e.target.value })}
                                placeholder="0.15"
                                inputMode="decimal"
                            />
                        </Field>
                    )}
                    <Field label="Quota fissa mensile (€)" testid="field-fissa">
                        <input
                            className="field"
                            value={data.fissa}
                            onChange={(e) => setData({ ...data, fissa: e.target.value })}
                            placeholder="12.00"
                            inputMode="decimal"
                        />
                    </Field>
                    <Field
                        label={`Consumi annui (${tipo === "gas" ? "Smc" : "kWh"}) *`}
                        testid="field-consumi"
                    >
                        <input
                            className="field"
                            value={data.consumi}
                            onChange={(e) => setData({ ...data, consumi: e.target.value })}
                            placeholder={tipo === "gas" ? "800" : "2700"}
                            inputMode="decimal"
                        />
                    </Field>
                </div>

                {hasData && (
                    <div
                        className="mt-6 bg-[#0C4650] text-white rounded-xl p-5 flex items-center justify-between flex-wrap gap-3"
                        data-testid="current-monthly-estimate"
                    >
                        <div>
                            <div className="text-[10px] tracking-widest text-[#91C6C0] font-bold">
                                STIMA MENSILE ATTUALE
                            </div>
                            <div className="text-3xl font-black text-[#C7FF2C] mt-0.5">
                                € {currentMonthly.toFixed(2)}
                            </div>
                        </div>
                        <div className="text-xs text-white/60">
                            Calcolato da: quota fissa + (€/{tipo === "gas" ? "Smc" : "kWh"} × consumi annui) ÷ 12
                        </div>
                    </div>
                )}
            </div>

            {/* Step 1.5: Documenti necessari + multi-upload */}
            <RequiredDocsAndUpload
                clientDocs={clientDocs}
                uploadingDoc={uploadingDoc}
                onUpload={handleClientDocUpload}
                onRemove={removeClientDoc}
            />

            {/* Step 2: offers */}
            <div className="card p-6 sm:p-8">
                <div className="flex items-center justify-between mb-5">
                    <h2 className="text-xl font-black text-[#0C4650]">
                        2. Scegli l'offerta migliore
                    </h2>
                    <div className="text-sm text-gray-500">{offers.length} offerte disponibili</div>
                </div>

                {offers.length === 0 ? (
                    <p className="text-center py-10 text-gray-500">
                        Nessuna offerta {tipo} in catalogo.
                    </p>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {offers.map((o) => {
                            const savings = calcSavings(o);
                            const isSel = selectedOfferId === o.id;
                            return (
                                <button
                                    key={o.id}
                                    type="button"
                                    onClick={() => setSelectedOfferId(o.id)}
                                    data-testid={`energy-offer-${o.id}`}
                                    className={`text-left border-2 rounded-2xl p-5 transition-all ${
                                        isSel
                                            ? "border-[#0C4650] bg-[#0C4650] text-white shadow-lg"
                                            : "border-gray-200 bg-white text-[#0C4650] hover:border-[#C7FF2C] hover:-translate-y-0.5"
                                    }`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="min-w-0">
                                            <div
                                                className={`text-[10px] tracking-widest font-bold ${isSel ? "text-[#C7FF2C]" : "text-[#898A8D]"}`}
                                            >
                                                {o.operatore.toUpperCase()}
                                            </div>
                                            <div className="font-black text-base mt-0.5 truncate">
                                                {o.plan_name}
                                            </div>
                                        </div>
                                        {o.green_energy && (
                                            <div
                                                className={`ml-2 flex-shrink-0 inline-flex items-center gap-1 text-[9px] font-bold px-2 py-1 rounded-full ${isSel ? "bg-[#C7FF2C]/20 text-[#C7FF2C]" : "bg-emerald-100 text-emerald-700"}`}
                                            >
                                                <Leaf className="w-2.5 h-2.5" /> GREEN
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-3 flex items-baseline gap-2">
                                        <span className="text-2xl font-black">
                                            € {(tipo === "gas" ? o.price_per_smc : o.price_per_kwh)?.toFixed(3)}
                                        </span>
                                        <span
                                            className={`text-xs ${isSel ? "text-white/60" : "text-gray-500"}`}
                                        >
                                            /{tipo === "gas" ? "Smc" : "kWh"}
                                        </span>
                                    </div>
                                    <div className={`text-xs ${isSel ? "text-white/70" : "text-gray-600"}`}>
                                        + € {o.monthly_fee?.toFixed(2)} fissa/mese
                                    </div>

                                    {o.promo_label && (
                                        <div
                                            className={`mt-2 inline-block text-[10px] font-bold px-2 py-1 rounded-full ${isSel ? "bg-[#C7FF2C] text-[#0C4650]" : "bg-[#C7FF2C]/40 text-[#0C4650]"}`}
                                        >
                                            {o.promo_label}
                                        </div>
                                    )}

                                    {savings && savings.saving > 0 && (
                                        <div
                                            className={`mt-4 pt-3 border-t ${isSel ? "border-white/20" : "border-gray-100"}`}
                                        >
                                            <div className="flex items-center gap-1 text-emerald-500 font-bold text-xs">
                                                <TrendingDown className="w-3 h-3" />
                                                Risparmio stimato
                                            </div>
                                            <div className="text-lg font-black mt-0.5">
                                                € {savings.saving.toFixed(2)}/mese
                                            </div>
                                            <div className={`text-xs ${isSel ? "text-white/60" : "text-gray-500"}`}>
                                                circa € {savings.annualSaving.toFixed(0)} all'anno
                                            </div>
                                        </div>
                                    )}

                                    {isSel && (
                                        <div className="mt-3 inline-flex items-center gap-1 text-[#C7FF2C] font-bold text-xs">
                                            <CheckCircle2 className="w-3 h-3" /> Selezionata
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Step 3: submit */}
            {selectedOfferId && (
                <div className="card p-6 sm:p-8 border-[#C7FF2C] border-2" data-testid="submit-section">
                    <h2 className="text-xl font-black text-[#0C4650]">
                        3. Invia richiesta di switch
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                        La sede riceverà la richiesta e ti tornerà con la proposta definitiva.
                    </p>
                    <div className="mt-5 grid sm:grid-cols-2 gap-4">
                        <Field label="Telefono cliente" testid="field-client-phone">
                            <input
                                className="field"
                                value={clientPhone}
                                onChange={(e) => setClientPhone(e.target.value)}
                                placeholder="+39 ..."
                            />
                        </Field>
                        <Field label="Email cliente" testid="field-client-email">
                            <input
                                type="email"
                                className="field"
                                value={clientEmail}
                                onChange={(e) => setClientEmail(e.target.value)}
                            />
                        </Field>
                    </div>
                    <div className="mt-6 flex justify-end">
                        <button
                            onClick={submit}
                            disabled={submitting}
                            className="btn-primary"
                            data-testid="submit-switch"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Sparkles className="w-4 h-4" />
                            )}
                            Invia richiesta di switch
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

const Field = ({ label, children, testid }) => (
    <div data-testid={testid}>
        <label className="label">{label}</label>
        {children}
    </div>
);

const StatBox = ({ icon, label, value, highlight, testid }) => (
    <div
        className={`rounded-xl p-4 backdrop-blur ${
            highlight ? "bg-[#C7FF2C] text-[#0C4650]" : "bg-white/10 text-white border border-white/10"
        }`}
        data-testid={testid}
    >
        <div className={`flex items-center gap-1.5 text-[10px] tracking-widest font-bold ${highlight ? "text-[#0C4650]/70" : "text-[#91C6C0]"}`}>
            {icon}
            {label}
        </div>
        <div className={`text-2xl font-black mt-1 ${highlight ? "" : "text-[#C7FF2C]"}`}>
            {value}
        </div>
    </div>
);

const TypeBtn = ({ active, onClick, icon, label, testid }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition-all ${
            active
                ? "bg-[#0C4650] text-white"
                : "bg-white border border-gray-200 text-[#0C4650] hover:border-[#0C4650]"
        }`}
    >
        {icon}
        {label}
    </button>
);

const TabBtn = ({ active, onClick, label, testid }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`px-5 py-3 font-bold text-sm transition-all relative ${
            active ? "text-[#0C4650]" : "text-gray-400 hover:text-[#0C4650]"
        }`}
    >
        {label}
        {active && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#C7FF2C]" />
        )}
    </button>
);


const fmtEur = (v, dp = 2) =>
    typeof v === "number"
        ? v.toLocaleString("it-IT", { minimumFractionDigits: dp, maximumFractionDigits: dp })
        : "—";

const BillBreakdownPanel = ({ data, tipo, expanded, onToggle }) => {
    const seller = data?.venditore || {};
    const reg = data?.regolate || {};
    const unit = tipo === "gas" ? "Smc" : "kWh";
    const sellerVar = seller.quota_variabile_eur_per_unit;
    const sellerFixed = seller.quota_fissa_eur_per_mese;

    return (
        <div
            className="mt-4 rounded-2xl border border-[#C7FF2C]/40 bg-gradient-to-br from-[#C7FF2C]/10 to-white overflow-hidden"
            data-testid="bill-breakdown-panel"
        >
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#C7FF2C]/5"
                data-testid="bill-breakdown-toggle"
            >
                <div className="flex items-center gap-2">
                    <span className="text-[10px] tracking-[0.28em] font-black text-[#0C4650]">
                        BREAKDOWN BOLLETTA
                    </span>
                    {data.low_confidence && (
                        <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                            bassa confidenza
                        </span>
                    )}
                </div>
                <span className="text-xs font-bold text-[#0C4650]">
                    {expanded ? "Nascondi −" : "Mostra +"}
                </span>
            </button>

            {expanded && (
                <div className="px-4 pb-4 space-y-4">
                    {/* Top: comparable seller quotas */}
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div className="rounded-xl bg-[#0C4650] text-white p-4">
                            <div className="text-[10px] tracking-widest font-bold text-[#C7FF2C]">
                                QUOTA ENERGIA · VENDITORE
                            </div>
                            <div className="text-2xl font-black mt-1">
                                {sellerVar != null ? `${fmtEur(sellerVar, 4)} €/${unit}` : "—"}
                            </div>
                            <div className="text-[11px] text-[#91C6C0] mt-1">
                                Componente confrontabile con altre offerte
                            </div>
                        </div>
                        <div className="rounded-xl bg-[#0C4650] text-white p-4">
                            <div className="text-[10px] tracking-widest font-bold text-[#C7FF2C]">
                                QUOTA FISSA · VENDITORE
                            </div>
                            <div className="text-2xl font-black mt-1">
                                {sellerFixed != null ? `${fmtEur(sellerFixed)} €/mese` : "—"}
                            </div>
                            <div className="text-[11px] text-[#91C6C0] mt-1">
                                PCV / QVD del fornitore
                            </div>
                        </div>
                    </div>

                    {/* Seller items detailed */}
                    {Array.isArray(seller.voci) && seller.voci.length > 0 && (
                        <div>
                            <div className="text-[11px] tracking-widest font-bold text-[#0C4650] mb-2">
                                VOCI VENDITORE (CONFRONTABILI)
                            </div>
                            <div className="space-y-1">
                                {seller.voci.map((v, i) => (
                                    <BreakdownRow
                                        key={`s-${i}`}
                                        label={v.label}
                                        value={v.valore}
                                        unit={v.unita}
                                        accent
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Regulated section */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-[11px] tracking-widest font-bold text-gray-500">
                                VOCI REGOLATE ARERA · IMPOSTE
                            </div>
                            <span className="text-[10px] font-bold text-gray-400">
                                NON CONFRONTABILI
                            </span>
                        </div>
                        <div className="space-y-1 opacity-80">
                            {(reg.trasporto_distribuzione || []).map((v, i) => (
                                <BreakdownRow
                                    key={`t-${i}`}
                                    label={`🚚 ${v.label}`}
                                    value={v.valore}
                                    unit={v.unita}
                                />
                            ))}
                            {(reg.oneri_sistema || []).map((v, i) => (
                                <BreakdownRow
                                    key={`o-${i}`}
                                    label={`⚙ ${v.label}`}
                                    value={v.valore}
                                    unit={v.unita}
                                />
                            ))}
                            {reg.imposte?.accise_eur_per_unit != null && (
                                <BreakdownRow
                                    label="💰 Accise"
                                    value={reg.imposte.accise_eur_per_unit}
                                    unit={`€/${unit}`}
                                />
                            )}
                            {reg.imposte?.iva_pct != null && (
                                <BreakdownRow
                                    label="💰 IVA"
                                    value={reg.imposte.iva_pct}
                                    unit="%"
                                />
                            )}
                            {reg.imposte?.canone_rai_eur_per_mese != null && (
                                <BreakdownRow
                                    label="📺 Canone RAI"
                                    value={reg.imposte.canone_rai_eur_per_mese}
                                    unit="€/mese"
                                />
                            )}
                        </div>
                    </div>

                    {(data.periodo_bolletta || data.totale_bolletta_eur != null) && (
                        <div className="pt-3 border-t border-[#0C4650]/10 grid sm:grid-cols-2 gap-2 text-xs">
                            {data.periodo_bolletta && (
                                <div className="text-gray-500">
                                    Periodo:{" "}
                                    <span className="font-bold text-[#0C4650]">
                                        {data.periodo_bolletta}
                                    </span>
                                </div>
                            )}
                            {data.totale_bolletta_eur != null && (
                                <div className="text-gray-500 sm:text-right">
                                    Totale fattura:{" "}
                                    <span className="font-bold text-[#0C4650]">
                                        € {fmtEur(data.totale_bolletta_eur)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    <p className="text-[11px] text-gray-500 leading-relaxed">
                        Per confrontare due offerte di mercato libero contano <strong>solo</strong>{" "}
                        le voci del venditore qui evidenziate in scuro: trasporto, distribuzione,
                        oneri di sistema e imposte sono fissati da ARERA e identici tra tutti i
                        fornitori a parità di tipologia di utenza.
                    </p>
                </div>
            )}
        </div>
    );
};

const BreakdownRow = ({ label, value, unit, accent }) => (
    <div
        className={`flex items-center justify-between text-sm px-3 py-1.5 rounded ${
            accent ? "bg-[#C7FF2C]/30" : "bg-white/60"
        }`}
    >
        <span className="text-gray-700">{label || "—"}</span>
        <span
            className={`font-bold ${accent ? "text-[#0C4650]" : "text-gray-700"}`}
        >
            {value != null ? `${fmtEur(value, 4)} ${unit || ""}`.trim() : "—"}
        </span>
    </div>
);

const REQUIRED_DOCS = [
    { icon: IdCard, label: "Documento di identità valido", hint: "Carta d'identità o passaporto, fronte/retro" },
    { icon: Heart, label: "Tessera sanitaria", hint: "Codice fiscale leggibile" },
    { icon: Mail, label: "Email del cliente", hint: "Per comunicazioni dal nuovo gestore" },
    { icon: Phone, label: "Numero di cellulare", hint: "Per OTP di firma e contatto" },
    { icon: CreditCard, label: "IBAN", hint: "Per il pagamento con domiciliazione" },
    { icon: Receipt, label: "Bolletta dell'attuale gestore", hint: "Anche già caricata sopra in 'Carica bolletta'" },
];

const fmtSize = (b) => {
    if (b == null) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const RequiredDocsAndUpload = ({ clientDocs, uploadingDoc, onUpload, onRemove }) => (
    <div className="card p-6 sm:p-8" data-testid="required-docs-section">
        <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#C7FF2C] flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-[#0C4650]" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
                <h2 className="text-xl font-black text-[#0C4650]">
                    Documenti del cliente per lo switch
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                    Per completare la pratica di switch luce/gas servono i seguenti
                    documenti. Caricali tutti insieme qui sotto, in qualsiasi formato.
                </p>
            </div>
        </div>

        {/* Lista documenti necessari */}
        <div className="mt-5 rounded-2xl border-2 border-[#C7FF2C]/40 bg-gradient-to-br from-[#C7FF2C]/10 to-white p-5"
             data-testid="required-docs-checklist">
            <div className="text-[10px] tracking-[0.28em] font-black text-[#0C4650] mb-3">
                CHECKLIST OBBLIGATORIA
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
                {REQUIRED_DOCS.map((d, i) => {
                    const Icon = d.icon;
                    return (
                        <div key={d.label} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-white/60"
                             data-testid={`required-doc-${i}`}>
                            <div className="w-8 h-8 rounded-lg bg-[#0C4650] text-[#C7FF2C] flex items-center justify-center shrink-0">
                                <Icon className="w-4 h-4" strokeWidth={2.5} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="font-bold text-[#0C4650] text-sm leading-tight">
                                    {d.label}
                                </div>
                                <div className="text-[11px] text-gray-500 leading-tight mt-0.5">
                                    {d.hint}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>

        {/* Multi-upload */}
        <div className="mt-5">
            <label
                className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#0C4650]/30 hover:border-[#0C4650] rounded-xl py-7 px-4 cursor-pointer transition-colors bg-[#f7f7f5]"
                data-testid="client-docs-dropzone"
            >
                {uploadingDoc ? (
                    <>
                        <Loader2 className="w-6 h-6 text-[#0C4650] animate-spin" />
                        <span className="text-sm font-semibold text-[#0C4650]">
                            Caricamento in corso…
                        </span>
                    </>
                ) : (
                    <>
                        <Plus className="w-6 h-6 text-[#0C4650]" strokeWidth={2.5} />
                        <span className="text-sm font-bold text-[#0C4650]">
                            Carica documenti del cliente
                        </span>
                        <span className="text-xs text-gray-500">
                            Più file in un colpo · PDF, JPG, PNG, HEIC, DOC, XLS, ZIP · max 25 MB ciascuno
                        </span>
                    </>
                )}
                <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={onUpload}
                    disabled={uploadingDoc}
                    data-testid="client-docs-input"
                />
            </label>
        </div>

        {/* Anteprima documenti caricati */}
        {clientDocs.length > 0 && (
            <div className="mt-4" data-testid="client-docs-list">
                <div className="text-[10px] tracking-[0.28em] font-black text-[#0C4650] mb-2">
                    {clientDocs.length} {clientDocs.length === 1 ? "DOCUMENTO" : "DOCUMENTI"} CARICATI
                </div>
                <div className="space-y-2">
                    {clientDocs.map((d) => (
                        <div key={d.file_id}
                             className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border-2 border-emerald-200"
                             data-testid={`client-doc-${d.file_id}`}>
                            <div className="w-9 h-9 rounded-lg bg-emerald-500 text-white flex items-center justify-center shrink-0">
                                <CheckCircle2 className="w-5 h-5" strokeWidth={2.5} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-emerald-800 text-sm truncate">
                                    {d.filename}
                                </div>
                                <div className="text-[11px] text-emerald-700/80">
                                    {fmtSize(d.size)} · {d.content_type || "file"}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => onRemove(d.file_id)}
                                className="w-8 h-8 rounded-lg bg-white text-red-500 hover:bg-red-50 flex items-center justify-center shrink-0"
                                data-testid={`remove-doc-${d.file_id}`}
                                aria-label="Rimuovi"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        )}
    </div>
);
